import React from 'react';
import { Q } from '@nozbe/watermelondb';
import { useDatabase } from '@nozbe/watermelondb/hooks';

import { Book } from '@src/database/models';
import useIsMounted from '@src/hooks/useIsMounted';
import { PrimeBook } from '@src/types';

export const useBooks = () => {
  const database = useDatabase();
  const isMounted = useIsMounted();
  const [data, setData] = React.useState<Book[]>([]);

  React.useEffect(() => {
    try {
      (async function () {
        setData(
          await database.collections
            .get<Book>(Book.table)
            .query(Q.where('is_downloaded', true))
            .fetch()
        );
      })();
    } catch (error) {
      console.log(error);
    }
  }, [database.collections, isMounted]);

  return React.useMemo(() => ({ data }), [data]);
};

export const useBook = (id: string) => {
  const database = useDatabase();
  const isMounted = useIsMounted();
  const [data, setData] = React.useState<Book>();

  React.useEffect(() => {
    const fetch = async () => {
      if (isMounted) {
        setData(await database.collections.get<Book>(Book.table).find(id));
      }
    };

    fetch().catch(console.log);
  }, [database.collections, id, isMounted]);

  return React.useMemo(() => ({ data }), [data]);
};

export const useAddOrReturnBook = () => {
  const database = useDatabase();
  return React.useCallback(
    async (data: PrimeBook) => {
      const collection = database.collections.get<Book>(Book.table);
      try {
        return await collection.find(data.id);
      } catch (e) {
        return await database.action<Book>(
          async () =>
            await collection.create((record) => {
              record._raw.id = data.id;
              record.title = data.title;
              record.publisher = data.publisher;
              record.translator = data.translator;
              record.language = data.language;
              record.pagesCount = data.pagesCount;
              record.score = data.score;
              record.scoreCount = data.scoreCount;
              record.isBookmarked = data.bookmarked;
              record.type = data.type;
              record.status = data.status;
              record.categories = data.categories;
              record.labels = data.labels;
              record.coverUrl = data?.cover?.source?.uri ?? '';
              record.isDownloaded = false;
            })
        );
      }
    },
    [database]
  );
};
